import { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { 
  LayoutDashboard, 
  RefreshCw,
  Search, 
  ArrowRightLeft, 
  History, 
  Settings, 
  Users, 
  Key, 
  LogOut, 
  User, 
  PackageCheck, 
  ClipboardList, 
  Package,
  ChevronDown,
  ChevronRight,
  Factory,
  Boxes,
  CheckCircle2,
  Database,
  Hammer,
  Zap,
  Droplets,
  Flame,
  Shield,
  Gauge,
  Users2,
  Settings2,
  Cog,
  FileSpreadsheet,
  Bell,
  AlertTriangle,
  Activity,
  Ship,
  FileText,
  RotateCcw,
  MapPin,
  Building,
  Building2,
  Menu,
  X,
  Trash2,
  Mail,
  PanelLeftClose,
  PanelLeft,
  Truck,
  Wrench,
  BarChart3,
  Calendar,
  Layers,
  Warehouse,
  CalendarDays,
  DollarSign,
  Container,
  ScanLine,
  ShieldAlert,
  GitBranch,
  Tag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import axios from "axios";
import { toast } from "sonner";
import InstallPrompt from "@/components/InstallPrompt";
import GlobalCalculator from "@/components/GlobalCalculator";
import { NotesFloater } from "@/components/notes/NotesFloater";
import GlobalArticleSearch from "@/components/GlobalArticleSearch";
import ChatBubble from "@/components/ChatBubble";
import SqlLinkBadge from "@/components/SqlLinkBadge";
import { NavShortcutsProvider, useNavShortcuts } from "@/components/NavShortcuts";
import NavCommandPalette from "@/components/NavCommandPalette";
import { Star, Clock, Command as CommandIcon } from "lucide-react";

// Dynamic icon component
const DynamicIcon = ({ name, className }) => {
  const IconComponent = LucideIcons[name];
  if (!IconComponent) return <LucideIcons.Circle className={className} />;
  return <IconComponent className={className} />;
};

// ─── Star-pin button rendered inside each sidebar item ─────────────
function StarPinButton({ item, section }) {
  const { pinnedSet, togglePin } = useNavShortcuts();
  const active = pinnedSet.has(item.path);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        togglePin(item.path);
      }}
      title={active ? "Remove from favorites" : "Pin to favorites"}
      data-testid={`nav-pin-${item.id || item.path}`}
      className={
        "ml-1 p-1 rounded transition-colors flex-shrink-0 " +
        (active
          ? "text-rose-500 hover:text-rose-600"
          : "text-zinc-300 hover:text-rose-500 opacity-0 group-hover:opacity-100")
      }
    >
      <Star className={`w-3.5 h-3.5 ${active ? "fill-current" : ""}`} />
    </button>
  );
}

// ─── Cmd+K launcher + Favorites + Recent (top of sidebar) ─────────
function SidebarShortcuts({ isCollapsed }) {
  const { pinned, recent, openPalette, gotoRoute } = useNavShortcuts();

  const trigger = (
    <button
      type="button"
      onClick={openPalette}
      data-testid="sidebar-cmdk-btn"
      className={
        "w-full flex items-center gap-2 rounded-md border border-dashed " +
        "border-zinc-300 hover:border-indigo-400 hover:bg-indigo-50 " +
        "text-xs text-zinc-500 hover:text-indigo-700 transition-colors " +
        (isCollapsed ? "justify-center p-2" : "px-2.5 py-1.5")
      }
    >
      <CommandIcon className="w-4 h-4 flex-shrink-0" />
      {!isCollapsed && (
        <>
          <span className="flex-1 text-left">Jump to module…</span>
          <kbd className="text-[9px] font-mono bg-zinc-100 border border-zinc-200 rounded px-1 py-0.5">
            Ctrl K
          </kbd>
        </>
      )}
    </button>
  );

  if (isCollapsed) {
    // In collapsed mode: just the palette launcher (icon-only).
    return <div className="mb-2">{trigger}</div>;
  }

  return (
    <div className="mb-2 space-y-2">
      {trigger}
      {pinned.length > 0 && (
        <div>
          <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-rose-500 flex items-center gap-1">
            <Star className="w-3 h-3 fill-current" /> Favorites
          </div>
          <div className="space-y-0.5">
            {pinned.map((p) => (
              <button
                key={p.route}
                onClick={() => gotoRoute(p.route)}
                data-testid={`sidebar-pinned-${p.route}`}
                className="w-full flex items-center gap-2 nav-item sub-item touch-target pl-3 pr-2"
              >
                <DynamicIcon name={p.icon} className="w-4 h-4 flex-shrink-0 text-rose-500" />
                <span className="truncate text-xs flex-1 text-left">{p.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {recent.length > 0 && (
        <div>
          <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-indigo-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Recent
          </div>
          <div className="space-y-0.5">
            {recent.slice(0, 5).map((r) => (
              <button
                key={r.route}
                onClick={() => gotoRoute(r.route)}
                data-testid={`sidebar-recent-${r.route}`}
                className="w-full flex items-center gap-2 nav-item sub-item touch-target pl-3 pr-2"
              >
                <DynamicIcon name={r.icon} className="w-4 h-4 flex-shrink-0 text-indigo-500" />
                <span className="truncate text-xs flex-1 text-left">{r.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="border-t border-zinc-200 pt-1" />
    </div>
  );
}

const LOGO_URL = "https://customer-assets.emergentagent.com/job_eurokon-move/artifacts/slgvzlik_logo.png";
const API = `${typeof window !== 'undefined' ? window.location.origin : ''}/api`;

export default function Layout({ user, onLogout, hasPermission }) {
  const navigate = useNavigate();
  
  // Sidebar collapsed state - persist in localStorage.
  // Default: COLLAPSED (users asked for a cleaner landing view given the
  // growing number of modules). Respect prior explicit preference if present.
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved === null) return true;      // first visit → collapsed
    return saved !== 'false';             // any non-"false" value → collapsed
  });
  
  const [expandedMenus, setExpandedMenus] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationOpen, setNotificationOpen] = useState(false);
  
  // Dynamic sidebar config
  const [sidebarConfig, setSidebarConfig] = useState(null);
  const [appVersion, setAppVersion] = useState("");

  // Fetch sidebar config
  const fetchSidebarConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/sidebar/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSidebarConfig(response.data);
      
      // Initialize expanded menus: collapsed by default (user asked for
      // cleaner sidebar). Respect any per-user preference in localStorage
      // keyed by section id.
      const expanded = {};
      response.data.sections?.forEach(section => {
        const saved = localStorage.getItem(`sidebarSection:${section.id}`);
        expanded[section.id] = saved === 'true';
      });
      setExpandedMenus(expanded);
      return true;
    } catch (error) {
      console.error("Failed to fetch sidebar config:", error);
      return false;
    }
  }, []);

  // Fetch app version
  const fetchVersion = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/version`);
      setAppVersion(response.data.version || "");
    } catch (error) {
      console.error("Failed to fetch version");
    }
  }, []);

  useEffect(() => {
    fetchVersion();
    // Backend may still be starting right after a deploy: keep retrying until the real menu arrives
    let cancelled = false, attempt = 0, timer = null;
    const tryFetch = async () => {
      if (cancelled) return;
      const ok = await fetchSidebarConfig();
      if (!ok && !cancelled) {
        attempt += 1;
        timer = setTimeout(tryFetch, Math.min(3000 * attempt, 20000));
      }
    };
    tryFetch();
    const onFocus = () => { if (!document.hidden) fetchSidebarConfig(); };
    window.addEventListener("online", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener("online", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [fetchSidebarConfig, fetchVersion]);

  // Save collapsed state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed);
  }, [isCollapsed]);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch notifications");
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleVerifyTransfer = async (transferId, notificationId) => {
    try {
      await axios.post(`${API}/transfers/${transferId}/verify`);
      toast.success("Transfer verified successfully!");
      fetchNotifications();
      setNotificationOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify transfer");
    }
  };

  const toggleMenu = (menu) => {
    if (isCollapsed) {
      setIsCollapsed(false);
    }
    setExpandedMenus(prev => {
      const next = !prev[menu];
      // Persist per-section preference so it survives reloads
      try { localStorage.setItem(`sidebarSection:${menu}`, String(next)); } catch { /* ignore */ }
      return { ...prev, [menu]: next };
    });
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-500";
      case "entry": return "bg-orange-500";
      case "viewer": return "bg-zinc-500";
      default: return "bg-zinc-500";
    }
  };

  // ============ REORGANIZED MENU STRUCTURE ============

  // Quick Access (always visible)
  const quickAccessItems = [
    { to: "/", icon: LayoutDashboard, label: "Dashboard", permission: "view" },
    { to: "/search", icon: Search, label: "Search", permission: "view" },
  ];

  // Material Flow (Transfer & Receiving)
  const materialFlowItems = [
    { to: "/transfer", icon: ArrowRightLeft, label: "New Transfer", permission: "entry" },
    { to: "/receive", icon: PackageCheck, label: "Receive Item", permission: "entry" },
    { to: "/transfer-rejection", icon: RotateCcw, label: "Rejection Entry", permission: "entry" },
    { to: "/history", icon: History, label: "Transfer History", permission: "view" },
    { to: "/received-history", icon: ClipboardList, label: "Received History", permission: "view" },
    { to: "/consumable-issue", icon: Boxes, label: "Consumable Issue", permission: "entry" },
    { to: "/consumable-history", icon: History, label: "Consumable History", permission: "view" },
  ];

  // Manufacturing (Press Shop, Welding, etc.)
  const manufacturingItems = [
    { to: "/production/press-shop", icon: Hammer, label: "Press Shop", permission: "view" },
    { to: "/production/welding", icon: Zap, label: "Welding", permission: "view" },
    { to: "/production/plating", icon: Droplets, label: "Plating", permission: "view" },
    { to: "/production/hot-dip", icon: Flame, label: "Hot Dip", permission: "view" },
    { to: "/maintenance", icon: Wrench, label: "Repair & Maintenance", permission: "view", permissionModule: "maintenance" },
  ];

  // PPC (Production Planning and Control)
  const ppcItems = [
    { to: "/ppc/process-master", icon: Layers, label: "Process Master", permission: "view" },
    { to: "/ppc/monthly-scheduler", icon: Calendar, label: "Monthly Scheduler", permission: "view" },
    { to: "/ppc/planning", icon: CalendarDays, label: "Machine Planning", permission: "view" },
    { to: "/ppc/capacity", icon: BarChart3, label: "Capacity Planning", permission: "view" },
    { to: "/ppc/standards", icon: Settings, label: "Production Standards", permission: "view" },
  ];

  // Finished Goods
  const finishedGoodsItems = [
    { to: "/finished-production", icon: CheckCircle2, label: "Record Production", permission: "entry" },
    { to: "/finished-dashboard", icon: LayoutDashboard, label: "Dashboard", permission: "view" },
    { to: "/finished-history", icon: History, label: "History", permission: "view" },
    { to: "/finished-planning", icon: CalendarDays, label: "Planning", permission: "view" },
    { to: "/packing-balance", icon: FileSpreadsheet, label: "PACKING BALANCE", permission: "view" },
    { to: "/article-database", icon: Database, label: "Article Database", permission: "view", permissionModule: "article_database" },
    { to: "/packing-database", icon: Boxes, label: "Packing Database", permission: "view", permissionModule: "packing_database" },
    { to: "/packing-material-issue", icon: Package, label: "Packing Material Issue", permission: "view", permissionModule: "packing_material_issue" },
    { to: "/packing-dept-analysis", icon: Factory, label: "Packing Dept Analysis", permission: "view", permissionModule: "packing_dept_analysis" },
    { to: "/packing-stock-shortages", icon: AlertTriangle, label: "Stock Shortages", permission: "view", permissionModule: "packing_dept_analysis" },
    { to: "/pallet-production", icon: Boxes, label: "Pallet Production", permission: "view", permissionModule: "pallet_production" },
    { to: "/container-loading", icon: Container, label: "Container Loading", permission: "view", permissionModule: "pallet_production" },
    { to: "/loading-scan", icon: ScanLine, label: "Loading Scan", permission: "view", permissionModule: "pallet_production" },
  ];

  // Quality Control
  const qualityItems = [
    { to: "/quality/incoming-goods", icon: PackageCheck, label: "Incoming Goods", permission: "view" },
    { to: "/quality/breakdown-dashboard", icon: Activity, label: "Machine Status", permission: "view" },
    { to: "/quality/breakdown", icon: AlertTriangle, label: "Breakdown", permission: "view" },
    { to: "/quality/calibration", icon: Gauge, label: "Calibration", permission: "view" },
  ];

  // Shipment
  const shipmentItems = [
    { to: "/shipment/plans", icon: Ship, label: "Shipment Plans", permission: "entry" },
    { to: "/shipment/requirements", icon: Package, label: "Requirements", permission: "view" },
    { to: "/shipment/report", icon: FileText, label: "Report", permission: "view" },
    { to: "/shipment/erp-status", icon: ShieldAlert, label: "ERP Order Status", permission: "view" },
    { to: "/shipment/jrc-trace", icon: GitBranch, label: "JRC Back-track", permission: "view" },
    { to: "/shipment/batch-summary", icon: Tag, label: "Batch Summary", permission: "view" },
    { to: "/freight-cost", icon: Truck, label: "Freight Cost", permission: "entry" },
  ];

  // Reports
  const reportItems = [
    { to: "/reports/coating", icon: Droplets, label: "Coating Consumption", permission: "view" },
    { to: "/reports/production-analysis", icon: BarChart3, label: "Production Analysis", permission: "view" },
    { to: "/reports/production-salary", icon: DollarSign, label: "Production vs Salary", permission: "view", permissionModule: "production_salary" },
    { to: "/reports/order-status", icon: Package, label: "Order Status", permission: "view" },
    { to: "/reports/department-order-status", icon: Building2, label: "Dept Order Status", permission: "view" },
    { to: "/reports/wip-recon", icon: FileSpreadsheet, label: "WIP Qty Match", permission: "view" },
  ];

  // Admin (Settings, Masters, Users)
  const adminItems = [
    { to: "/masters/articles", icon: Database, label: "Article Master", permission: "settings" },
    { to: "/masters/operator", icon: Users2, label: "Operator Master", permission: "settings" },
    { to: "/masters/operation", icon: Settings2, label: "Operation Master", permission: "settings" },
    { to: "/masters/machine", icon: Cog, label: "Machine Master", permission: "settings" },
    { to: "/masters/instrument", icon: Gauge, label: "Instrument Master", permission: "settings" },
    { to: "/masters/parameters", icon: Settings2, label: "Parameter Master", permission: "settings" },
    { to: "/masters/locations", icon: MapPin, label: "Location Master", permission: "settings" },
    { to: "/masters/departments", icon: Building, label: "Department Master", permission: "settings" },
    { to: "/masters/suppliers", icon: Building2, label: "Supplier Master", permission: "settings" },
    { to: "/masters/pallet", icon: Boxes, label: "Pallet Master", permission: "view", permissionModule: "pallet_master" },
    { to: "/masters/dies", icon: Hammer, label: "Die & Mold Master", permission: "view", permissionModule: "die_master" },
    { to: "/masters/holidays", icon: Calendar, label: "Holiday Master", permission: "settings" },
    { to: "/masters/bom", icon: Layers, label: "BOM Master", permission: "settings" },
    { to: "/stock/location", icon: Warehouse, label: "Location Stock", permission: "view" },
    { to: "/users", icon: Users, label: "Users", permission: "manage_users" },
    { to: "/user-permissions", icon: Shield, label: "Permissions", permission: "manage_users" },
    { to: "/admin/deletion-logs", icon: Trash2, label: "Deletion Logs", permission: "manage_users" },
    { to: "/admin/email-reports", icon: Mail, label: "Email Reports", permission: "manage_users" },
    { to: "/admin/security", icon: Shield, label: "Security", permission: "manage_users" },
    { to: "/templates", icon: FileSpreadsheet, label: "Templates", permission: "view" },
    { to: "/sf-summary", icon: Package, label: "SF Summary", permission: "view" },
    { to: "/jrc-change", icon: Wrench, label: "JRC Change Tool", permission: "settings" },
    { to: "/backup-restore", icon: Database, label: "Backup", permission: "settings" },
    { to: "/settings", icon: Settings, label: "Settings", permission: "settings" },
  ];

  // Render nav item with tooltip for collapsed mode
  const renderNavItem = (item) => {
    if (!hasPermission(item.permission)) return null;
    
    const navLink = (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === "/"}
        className={({ isActive }) =>
          `nav-item touch-target ${isActive ? "active" : ""} ${isCollapsed ? "justify-center" : ""}`
        }
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <item.icon className="w-5 h-5 flex-shrink-0" />
        {!isCollapsed && <span className="truncate">{item.label}</span>}
      </NavLink>
    );

    if (isCollapsed) {
      return (
        <Tooltip key={item.to} delayDuration={0}>
          <TooltipTrigger asChild>
            {navLink}
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {item.label}
          </TooltipContent>
        </Tooltip>
      );
    }
    
    return navLink;
  };

  // Render sub nav item
  const renderSubNavItem = (item) => {
    // Check basic permission first
    if (!hasPermission(item.permission)) return null;
    
    // If permissionModule is specified, check module-specific permission
    if (item.permissionModule) {
      const userPermissions = user?.permissions || {};
      const modulePerms = userPermissions[item.permissionModule] || {};
      // Admin users have all permissions
      if (user?.role !== 'admin' && !modulePerms.view) return null;
    }

    const navLink = (
      <NavLink
        key={item.to}
        to={item.to}
        className={({ isActive }) =>
          `nav-item sub-item touch-target ${isCollapsed ? "pl-2 justify-center" : "pl-10"} ${isActive ? "active" : ""}`
        }
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <item.icon className="w-4 h-4 flex-shrink-0" />
        {!isCollapsed && <span className="truncate">{item.label}</span>}
      </NavLink>
    );

    if (isCollapsed) {
      return (
        <Tooltip key={item.to} delayDuration={0}>
          <TooltipTrigger asChild>
            {navLink}
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {item.label}
          </TooltipContent>
        </Tooltip>
      );
    }

    return navLink;
  };

  // Render expandable menu group
  const renderExpandableMenu = (title, Icon, menuKey, items, color) => {
    const hasVisibleItems = items.some(item => hasPermission(item.permission));
    if (!hasVisibleItems) return null;

    const menuButton = (
      <button
        onClick={() => toggleMenu(menuKey)}
        className={`nav-item touch-target w-full ${isCollapsed ? "justify-center" : "justify-between"} ${expandedMenus[menuKey] ? 'bg-zinc-100' : ''}`}
        data-testid={`menu-${menuKey}`}
      >
        <div className={`flex items-center ${isCollapsed ? "" : "gap-2"}`}>
          <Icon className={`w-5 h-5 flex-shrink-0 ${color}`} />
          {!isCollapsed && <span className="font-medium truncate">{title}</span>}
        </div>
        {!isCollapsed && (
          expandedMenus[menuKey] ? (
            <ChevronDown className="w-4 h-4 text-zinc-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
          )
        )}
      </button>
    );

    return (
      <div className="mb-1" key={menuKey}>
        {isCollapsed ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              {menuButton}
            </TooltipTrigger>
            <TooltipContent side="right" className="font-medium">
              {title}
            </TooltipContent>
          </Tooltip>
        ) : (
          menuButton
        )}
        {expandedMenus[menuKey] && !isCollapsed && (
          <div className="mt-1 space-y-0.5">
            {items.map(item => renderSubNavItem(item))}
          </div>
        )}
      </div>
    );
  };

  const handleNavClick = () => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <NavShortcutsProvider sidebarConfig={sidebarConfig}>
    <TooltipProvider>
      <div className="app-container">
        {/* Mobile Header */}
        <header className="mobile-header" data-testid="mobile-header">
          <button 
            className="hamburger-btn" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="hamburger-menu"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="flex items-center gap-2">
            <img 
              src={LOGO_URL} 
              alt="EUROKON" 
              className="h-8 w-auto object-contain"
            />
            <span className="font-heading font-bold text-lg uppercase hidden min-[430px]:inline">EUROKON</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
          <SqlLinkBadge compact />
          <Popover open={notificationOpen} onOpenChange={setNotificationOpen}>
            <PopoverTrigger asChild>
              <button className="relative p-2" data-testid="mobile-notification-btn">
                <Bell className="w-5 h-5 text-zinc-600" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              <div className="p-3 border-b bg-zinc-50">
                <h4 className="font-semibold text-sm">Pending Verifications</h4>
                <p className="text-xs text-zinc-500">Transfers awaiting your confirmation</p>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-zinc-500">
                    No pending verifications
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div key={notif.id} className="p-3 border-b hover:bg-zinc-50">
                      <p className="text-sm font-medium text-zinc-800">{notif.message}</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        From: {notif.created_by} • {new Date(notif.created_at).toLocaleString()}
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleVerifyTransfer(notif.transfer_id, notif.id)}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Verify Received
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>
          </div>
        </header>

        {/* Mobile Overlay */}
        {mobileMenuOpen && (
          <div 
            className="sidebar-overlay" 
            onClick={() => setMobileMenuOpen(false)}
            data-testid="sidebar-overlay"
          />
        )}

        {/* Sidebar */}
        <aside 
          className={`sidebar ${mobileMenuOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`} 
          data-testid="sidebar"
        >
          {/* Sidebar Header */}
          <div className={`sidebar-logo ${isCollapsed ? 'justify-center' : ''}`}>
            {!isCollapsed && (
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <img 
                  src={LOGO_URL} 
                  alt="EUROKON Logo" 
                  className="h-10 w-auto object-contain flex-shrink-0"
                  data-testid="sidebar-logo"
                />
                <p className="text-xs text-zinc-500 font-mono truncate">v1.0</p>
              </div>
            )}
            {isCollapsed && (
              <div className="flex flex-col items-center gap-1">
                <img 
                  src={LOGO_URL} 
                  alt="EUROKON" 
                  className="h-8 w-auto object-contain"
                />
                <SqlLinkBadge compact />
              </div>
            )}
            {/* Collapse toggle - desktop only */}
            <button 
              className="collapse-toggle hidden md:flex"
              onClick={toggleSidebar}
              data-testid="collapse-toggle"
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <PanelLeft className="w-5 h-5 text-zinc-500" />
              ) : (
                <PanelLeftClose className="w-5 h-5 text-zinc-500" />
              )}
            </button>
            {/* Close button for mobile */}
            <button 
              className="sidebar-close-btn"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="sidebar-close"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {!isCollapsed && (
            <div className="px-4 py-1.5 border-b border-zinc-200 flex items-center justify-between" data-testid="sidebar-sql-link-row">
              <SqlLinkBadge />
              <span className="text-[10px] text-zinc-400 font-mono">ERP link</span>
            </div>
          )}
          
          <nav className="sidebar-nav" onClick={handleNavClick}>
            {/* ⌨️  Cmd+K launcher + ⭐ Favorites + 🕒 Recent */}
            <SidebarShortcuts isCollapsed={isCollapsed} />

            {/* Dynamic Sidebar from Config */}
            {sidebarConfig?.sections?.filter(s => s.visible !== false).map((section, idx) => {
              // Skip admin-only sections for non-admins
              if (section.adminOnly && user?.role !== "admin") return null;
              
              const visibleItems = section.items?.filter(item => {
                if (item.visible === false) return false;
                if (!hasPermission(item.permission || "view")) return false;
                // Granular module-level permission (admin always passes)
                if (item.permissionModule && user?.role !== "admin") {
                  if (!hasPermission(item.permissionModule, "view")) return false;
                }
                return true;
              }) || [];
              
              if (visibleItems.length === 0) return null;
              
              return (
                <div key={section.id} className="mb-1">
                  {idx > 0 && <div className="my-2 border-t border-zinc-200"></div>}
                  
                  {/* Section with items */}
                  {section.items?.length > 0 ? (
                    <>
                      {/* Section Header (expandable) */}
                      {isCollapsed ? (
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => toggleMenu(section.id)}
                              className={`nav-item touch-target w-full justify-center ${expandedMenus[section.id] ? 'bg-zinc-100' : ''}`}
                              data-testid={`menu-${section.id}`}
                            >
                              <DynamicIcon name={section.icon} className="w-5 h-5 flex-shrink-0 text-zinc-600" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="font-medium">
                            {section.title}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <button
                          onClick={() => toggleMenu(section.id)}
                          className={`nav-item touch-target w-full justify-between ${expandedMenus[section.id] ? 'bg-zinc-100' : ''}`}
                          data-testid={`menu-${section.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <DynamicIcon name={section.icon} className="w-5 h-5 flex-shrink-0 text-zinc-600" />
                            <span className="font-medium truncate">{section.title}</span>
                          </div>
                          {expandedMenus[section.id] ? (
                            <ChevronDown className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                          )}
                        </button>
                      )}
                      
                      {/* Section Items */}
                      {expandedMenus[section.id] && !isCollapsed && (
                        <div className="mt-1 space-y-0.5">
                          {visibleItems.map(item => (
                            isCollapsed ? (
                              <Tooltip key={item.id} delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <NavLink
                                    to={item.path}
                                    className={({ isActive }) =>
                                      `nav-item sub-item touch-target pl-2 justify-center ${isActive ? "active" : ""}`
                                    }
                                    data-testid={`nav-${item.id}`}
                                    onClick={() => window.scrollTo(0, 0)}
                                  >
                                    <DynamicIcon name={item.icon} className="w-4 h-4 flex-shrink-0" />
                                  </NavLink>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="font-medium">
                                  {item.label}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <div key={item.id} className="group relative flex items-center">
                                <NavLink
                                  to={item.path}
                                  className={({ isActive }) =>
                                    `nav-item sub-item touch-target pl-10 pr-8 flex-1 ${isActive ? "active" : ""}`
                                  }
                                  data-testid={`nav-${item.id}`}
                                  onClick={() => { window.scrollTo(0, 0); document.querySelector('.main-content')?.scrollTo(0, 0); }}
                                >
                                  <DynamicIcon name={item.icon} className="w-4 h-4 flex-shrink-0" />
                                  <span className="truncate">{item.label}</span>
                                </NavLink>
                                <div className="absolute right-1 top-1/2 -translate-y-1/2">
                                  <StarPinButton item={item} section={section} />
                                </div>
                              </div>
                            )
                          ))}
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              );
            })}
            
            {/* Fallback static menu if no config loaded */}
            {!sidebarConfig && (
              <>
                {!isCollapsed && (
                  <div className="mx-3 mb-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800 flex items-center gap-1.5" data-testid="sidebar-loading-hint">
                    <RefreshCw className="w-3 h-3 animate-spin flex-shrink-0" /> Loading full menu… server waking up
                  </div>
                )}
                {quickAccessItems.map(item => renderNavItem(item))}
                <div className="my-2 border-t border-zinc-200"></div>
                {renderExpandableMenu("Material Flow", Truck, "materialFlow", materialFlowItems, "text-blue-600")}
                {renderExpandableMenu("Manufacturing", Factory, "manufacturing", manufacturingItems, "text-purple-600")}
                {renderExpandableMenu("PPC", CalendarDays, "ppc", ppcItems, "text-teal-600")}
                {renderExpandableMenu("Finished Goods", CheckCircle2, "finishedGoods", finishedGoodsItems, "text-emerald-600")}
                {renderExpandableMenu("Quality", Wrench, "quality", qualityItems, "text-cyan-600")}
                {renderExpandableMenu("Shipment", Ship, "shipment", shipmentItems, "text-indigo-600")}
                {renderExpandableMenu("Reports", BarChart3, "reports", reportItems, "text-amber-600")}
                <div className="my-2 border-t border-zinc-200"></div>
                {renderExpandableMenu("Admin & Settings", Settings, "admin", adminItems, "text-zinc-600")}
              </>
            )}
          </nav>
          
          {/* User Section */}
          <div className={`p-3 border-t border-zinc-200 ${isCollapsed ? 'px-2' : ''}`}>
            {/* Notification Bell */}
            <div className="mb-2">
              <Popover open={notificationOpen} onOpenChange={setNotificationOpen}>
                <PopoverTrigger asChild>
                  <button 
                    className={`w-full flex items-center gap-3 p-2 hover:bg-zinc-50 rounded transition-colors relative touch-target ${isCollapsed ? 'justify-center' : ''}`}
                    data-testid="notification-bell"
                  >
                    <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center relative flex-shrink-0">
                      <Bell className="w-5 h-5 text-amber-600" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          {unreadCount > 9 ? "9+" : unreadCount}
                        </span>
                      )}
                    </div>
                    {!isCollapsed && (
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium">Notifications</p>
                        <p className="text-xs text-zinc-500 truncate">
                          {unreadCount > 0 ? `${unreadCount} pending` : "No pending"}
                        </p>
                      </div>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <div className="p-3 border-b bg-zinc-50">
                    <h4 className="font-semibold text-sm">Pending Verifications</h4>
                    <p className="text-xs text-zinc-500">Transfers awaiting your confirmation</p>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-sm text-zinc-500">
                        No pending verifications
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div key={notif.id} className="p-3 border-b hover:bg-zinc-50">
                          <p className="text-sm font-medium text-zinc-800">{notif.message}</p>
                          <p className="text-xs text-zinc-500 mt-1">
                            From: {notif.created_by} • {new Date(notif.created_at).toLocaleString()}
                          </p>
                          <Button 
                            size="sm" 
                            className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleVerifyTransfer(notif.transfer_id, notif.id)}
                            data-testid={`verify-btn-${notif.transfer_id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Verify Received
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={`w-full flex items-center gap-3 p-2 hover:bg-zinc-50 rounded transition-colors touch-target ${isCollapsed ? 'justify-center' : ''}`} 
                  data-testid="user-menu"
                >
                  <div className="w-9 h-9 bg-zinc-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-zinc-600" />
                  </div>
                  {!isCollapsed && (
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium truncate">{user?.full_name || user?.username}</p>
                      <Badge className={`${getRoleBadgeColor(user?.role)} text-white text-[10px] px-1.5 py-0`}>
                        {user?.role?.toUpperCase()}
                      </Badge>
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.full_name || user?.username}</p>
                  <p className="text-xs text-zinc-500">@{user?.username}</p>
                  {user?.default_department && (
                    <p className="text-xs text-blue-600 mt-1">Dept: {user.default_department}</p>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <NavLink to="/change-password" className="cursor-pointer">
                    <Key className="w-4 h-4 mr-2" />
                    Change Password
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} className="text-red-600 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Version Display */}
            {appVersion && !isCollapsed && (
              <div className="mt-2 pt-2 border-t border-zinc-100 text-center">
                <span className="text-[10px] text-zinc-400 font-mono">v{appVersion}</span>
              </div>
            )}
          </div>
        </aside>
        
        {/* Main Content */}
        <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Outlet />
        </main>
        
        {/* Global Calculator — Alt+C to toggle anywhere */}
        <GlobalCalculator />
        <NotesFloater />

        {/* Global Article Search — code ↔ size/description, every page */}
        <GlobalArticleSearch />

        {/* PWA Install Prompt */}
        <InstallPrompt />

        {/* Floating in-app chat — bottom-right on every authenticated page */}
        <ChatBubble />

        {/* Ctrl/Cmd+K global module palette */}
        <NavCommandPalette />
      </div>
    </TooltipProvider>
    </NavShortcutsProvider>
  );
}
